import { useContext, useMemo } from "react";
import { Box, Col, HStack, Row, Text, Tooltip, VStack } from "@reverb-ui/react";
import { Flag } from "@reverb-ui/icons-react";
import { SortedTable } from "../../components/SortedTable";
import { MetricCard } from "../../components/MetricCard";
import { InsightsOnTagsContext } from "../context-api/InsightsOnTagsProvider";
import { ListCardSkeleton } from "presentation/components/Skeleton/CardSkeleton";
import { SortedTableSkeleton } from "presentation/components/Skeleton/SortedTableSkeleton";
import { panelsInfo } from "helper/panelsHelper";
import PanelContainer from "presentation/components/PanelContainer";

type TTagNameProps = {
  flagColor: string;
  id: string;
  converted: string;
};

const TagName = ({ id, flagColor, converted }: TTagNameProps) => (
  <HStack spacing={3}>
    <Text fontSize="sm" color="gray.700">
      {id}
    </Text>
    <Tooltip
      label={`CII: ${converted}`}
      bg="gray.100"
      color="gray.800"
      shouldWrapChildren
    >
      <Flag color={flagColor as any} variant="solid" size="md" />
    </Tooltip>
  </HStack>
);

const getFlagColor = (iconVariantionCII: "Above" | "Below" | "N/A") => {
  if (iconVariantionCII === "Above") return "red.500";
  if (iconVariantionCII === "Below") return "green.300";

  return "blue.500";
};

const insightsOnTagsInfo = panelsInfo.insightsOnTags;

export const InsightsOnTags = () => {
  const {
    requestParamsIndex,
    insightsOnTags,
    downloadingReports,
    downloadInsightsOnTagsCSV,
    isLoading,
  } = useContext(InsightsOnTagsContext);
  const insightsOnTagsRows = useMemo(() => {
    return insightsOnTags?.tags.dataRows.map((tag) => ({
      id: (
        <TagName
          id={tag.tagId}
          flagColor={getFlagColor(tag.iconAverageCII)}
          converted={tag.percentageConverted}
        />
      ),
      visitors: `${tag.visitors} (${tag.percentageVisitors})`,
      primary: `${tag.primary} (${tag.percentagePrimary})`,
      preConverted: `${tag.preConverted} (${tag.percentagePreConverted})`,
      converted: `${tag.converted} (${tag.percentageConverted})`,
    }));
  }, [insightsOnTags]);
  return (
    <PanelContainer
      panelId={insightsOnTagsInfo.htmlId}
      panelName={insightsOnTagsInfo.description}
      rangeName={insightsOnTags?.range}
      isLoading={isLoading}
      downloadOptions={{
        onClickGenerateCSV: downloadInsightsOnTagsCSV,
        downloadingReports: downloadingReports,
        panelInfo: insightsOnTagsInfo,
        filterStateIndex: requestParamsIndex,
      }}
    >
      <Row alignItems="start" position="relative">
        <Col col={!isLoading && insightsOnTags?.cards.length === 0 ? 0 : 3}>
          {isLoading ? (
            <ListCardSkeleton
              numberOfCards={2}
              spacingBetween={5}
              hasSubText={true}
              hasRecomendation={true}
            />
          ) : (
            <VStack w="full">
              {insightsOnTags?.cards.map((card, index) => (
                <MetricCard
                  key={index}
                  order={card.order}
                  iconName={card.iconTitle}
                  title={card.title}
                  description={`"${card.text}"`}
                  subDescription={card.subText}
                  insight={{
                    message: card.recommendation,
                    feedback: card.outcomes.toLowerCase() as any,
                  }}
                />
              ))}
            </VStack>
          )}
        </Col>
        <Col
          col={!isLoading && insightsOnTags?.cards.length === 0 ? 12 : 9}
          h="520px"
          className="col-sorted-table"
        >
          <Box
            bg="white"
            w="full"
            p={6}
            overflow="auto"
            className="sortedTable"
          >
            {isLoading ? (
              <SortedTableSkeleton numberOfRows={8} />
            ) : (
              <SortedTable
                dataColumns={insightsOnTags?.tags.dataColumns || []}
                dataRows={insightsOnTagsRows || []}
              />
            )}
          </Box>
        </Col>
      </Row>
    </PanelContainer>
  );
};
